import { SupportedChainId, SupportedL1ChainId } from 'config/chains'
import ms from 'ms.macro'

export enum NetworkType {
  L1,
}

interface BaseChainInfo {
  readonly networkType: NetworkType
  readonly blockWaitMsBeforeWarning?: number
  readonly docs: string
  readonly bridge?: string
  readonly explorer: string
  readonly infoLink: string
  readonly logoUrl: string
  readonly label: string
  readonly helpCenterUrl?: string
  readonly nativeCurrency: {
    name: string // e.g. 'Goerli ETH',
    symbol: string // e.g. 'gorETH',
    decimals: number // e.g. 18,
  }
  readonly color?: string
  readonly backgroundColor?: string
}

export interface L1ChainInfo extends BaseChainInfo {
  readonly networkType: NetworkType.L1
  readonly defaultListUrl?: string
}

export type ChainInfoMap = { readonly [chainId: number]: L1ChainInfo } & {
  readonly [chainId in SupportedL1ChainId]: L1ChainInfo
}

const CHAIN_INFO: ChainInfoMap = {
  [SupportedChainId.BEPOLIA]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms`2m`,
    docs: 'https://docs.kodiak.finance',
    explorer: 'https://testnet.berascan.com/',
    infoLink: import.meta.env.VITE_V3_ANALYTICS || '',
    label: 'Berachain Bepolia',
    logoUrl: 'https://raw.githubusercontent.com/berachain/default-token-list/main/src/assets/bera.png',
    nativeCurrency: { name: 'Berachain Token', symbol: 'BERA', decimals: 18 },
  },
  [SupportedChainId.BERA]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms`2m`,
    docs: 'https://docs.kodiak.finance',
    explorer: 'https://berascan.com/',
    infoLink: import.meta.env.VITE_V3_ANALYTICS || '',
    label: 'Berachain Mainnet',
    logoUrl: 'https://raw.githubusercontent.com/berachain/default-token-list/main/src/assets/bera.png',
    nativeCurrency: { name: 'Berachain Token', symbol: 'BERA', decimals: 18 },
  },
  [SupportedChainId.ROBINHOOD]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms`2m`,
    docs: 'https://docs.kodiak.finance',
    explorer: 'https://robin.etherscan.io/',
    infoLink: import.meta.env.VITE_V3_ANALYTICS || '',
    label: 'Robinhood Chain',
    logoUrl: 'https://assets.relay.link/icons/4663/light.png',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  },
}

export function getChainInfo(chainId: SupportedL1ChainId): L1ChainInfo
export function getChainInfo(chainId: SupportedChainId): L1ChainInfo
export function getChainInfo(
  chainId: SupportedChainId | SupportedL1ChainId | number | undefined
): L1ChainInfo | undefined

/**
 * Overloaded method for returning ChainInfo given a chainID
 * Return type varies depending on input type:
 * number | undefined -> returns chaininfo | undefined
 * SupportedChainId -> returns L1ChainInfo
 * SupportedL1ChainId -> returns L1ChainInfo
 */
export function getChainInfo(chainId: any): any {
  if (chainId) {
    return CHAIN_INFO[chainId] ?? undefined
  }
  return undefined
}
